import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Compass, FileText, Lock, ReceiptText, ShieldCheck, MessagesSquare } from "lucide-react";
import { Seo } from "@/lib/seo";
import { HeroLines, Reveal, FadeIn } from "@/lib/motion";
import { CtaLink, CtaBlock } from "@/components/cta";
import { Marquee } from "@/components/marquee";
import { SITE, CTAS, TRES_PUERTAS, METODO, COMPROMISOS, whatsappHref } from "@/content/site";
import { MOMENTOS } from "@/content/moments";
import { SERVICIOS } from "@/content/services";
import { GUIAS } from "@/content/guides";
import { IMAGES } from "@/content/images";

const COMPROMISO_ICONOS = [Compass, FileText, ReceiptText, ShieldCheck, Lock, MessagesSquare];

export default function Home() {
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <>
      <Seo
        title="Derecho para momentos de vida"
        description="Tulevik Legal acompaña a personas, familias y negocios en los momentos donde el derecho importa: casa, pareja, trabajo, patrimonio y legado. Cuéntanos tu situación."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: "Tulevik Legal",
          description: SITE.tagline,
          areaServed: SITE.alcance,
          url: typeof window !== "undefined" ? window.location.origin : undefined,
        }}
      />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden bg-parchment" data-testid="home-hero">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-14 sm:px-8 sm:pb-24 sm:pt-20 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <FadeIn delay={0.05}>
              <p className="eyebrow mb-6">Tulevik Legal · {SITE.alcance}</p>
            </FadeIn>
            <HeroLines
              lines={["Cada momento de", "tu vida tiene un asunto legal.", "Lo ordenamos contigo."]}
              className="font-serif text-[2.6rem] leading-[1.06] tracking-tight text-ink-deep sm:text-6xl lg:text-[4.4rem]"
            />
            <FadeIn delay={0.55}>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink-muted">
                Somos un despacho para personas, familias y negocios. Orientación clara primero,
                estrategia jurídica cuando la necesitas — por momento de vida, no por miedo.
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <CtaLink href={CTAS.principal.href} testId="hero-cta-principal" variant="primary">
                  {CTAS.principal.label}
                  <ArrowRight className="h-4 w-4" />
                </CtaLink>
                <CtaLink
                  href={whatsappHref()}
                  testId="hero-cta-secundaria"
                  variant="secondary"
                  external={!!SITE.WHATSAPP_NUMERO}
                >
                  {CTAS.secundaria.label}
                  <ArrowUpRight className="h-4 w-4" />
                </CtaLink>
              </div>
              <p className="mt-7 font-mono text-xs tracking-wide text-ink-muted">
                {SITE.disponibilidad}
              </p>
            </FadeIn>
          </div>

          <div className="lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <FadeIn delay={0.4} className="relative">
                <div className="border border-hairline bg-parchment-surface p-2.5">
                  <img
                    src={IMAGES.heroArquitectura}
                    alt="Arquitectura mexicana con luz cálida"
                    className="h-[320px] w-full object-cover sm:h-[440px]"
                    loading="eager"
                  />
                  <div className="flex items-center justify-between px-3 py-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-azul">
                      Ruta legal personal
                    </p>
                    <p className="font-mono text-[10px] text-ink-muted">Paso 01 — cuéntanos</p>
                  </div>
                </div>
              </FadeIn>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Tres puertas de entrada */}
      <section className="bg-parchment" data-testid="home-tres-puertas">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <Reveal>
            <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
              <h2 className="max-w-md font-serif text-3xl leading-tight tracking-tight text-ink-deep sm:text-4xl">
                Tres maneras de empezar
              </h2>
              <p className="max-w-sm text-ink-muted">
                No necesitas saber qué trámite es el tuyo. Elige la puerta que te quede más cerca.
              </p>
            </div>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3 lg:gap-8">
            {TRES_PUERTAS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1} className="h-full">
                <Link
                  to={p.href}
                  data-testid={`puerta-${p.n}-link`}
                  className={`group flex h-full flex-col border border-hairline p-8 transition-colors duration-300 hover:bg-parchment-surface ${
                    i === 2 ? "bg-ink-deep text-parchment hover:bg-ink-deep" : "bg-white/50"
                  }`}
                >
                  <span className={`font-mono text-xs tracking-[0.22em] ${i === 2 ? "text-salvia-marco" : "text-azul"}`}>
                    {p.n}
                  </span>
                  <h3 className={`mt-6 font-serif text-2xl sm:text-3xl ${i === 2 ? "text-parchment" : "text-ink-deep"}`}>
                    {p.titulo}
                  </h3>
                  <p className={`mt-3 flex-1 text-sm leading-relaxed ${i === 2 ? "text-parchment/70" : "text-ink-muted"}`}>
                    {p.desc}
                  </p>
                  <span className={`mt-8 inline-flex items-center gap-2 text-sm font-medium ${i === 2 ? "text-salvia-marco" : "text-azul"}`}>
                    {p.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Momentos de vida — lista editorial */}
      <section className="bg-parchment" data-testid="home-momentos">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <Reveal>
            <p className="eyebrow mb-5">Momentos de vida</p>
            <h2 className="mb-14 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-ink-deep sm:text-4xl lg:text-5xl">
              Empieza por lo que está pasando, no por el trámite
            </h2>
          </Reveal>
          <div>
            {MOMENTOS.map((m, i) => (
              <Reveal key={m.slug} delay={Math.min(i * 0.04, 0.2)} y={16}>
                <Link
                  to={`/momentos/${m.slug}`}
                  data-testid={`home-momento-link-${m.slug}`}
                  className="group flex items-baseline gap-5 border-t border-hairline py-6 transition-colors duration-300 hover:bg-parchment-surface/70 sm:items-center sm:gap-8 sm:px-4"
                >
                  <span className="font-mono text-xs text-azul">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1">
                    <span className="block font-serif text-2xl text-ink-deep transition-transform duration-300 group-hover:translate-x-1 sm:text-3xl">
                      {m.nombre}
                    </span>
                    <span className="mt-1 block text-sm text-ink-muted sm:text-base">{m.frase}</span>
                  </span>
                  <ArrowRight className="h-5 w-5 shrink-0 self-center text-ink-muted transition-all duration-300 group-hover:translate-x-1 group-hover:text-azul" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Método */}
      <section className="bg-ink-deep text-parchment" data-testid="home-metodo">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:py-32">
          <Reveal>
            <p className="eyebrow mb-5 text-salvia-marco">Nuestro método</p>
            <h2 className="mb-16 max-w-2xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              Un proceso que puedes entender desde el primer día
            </h2>
          </Reveal>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {METODO.map((paso, i) => (
              <Reveal key={paso.n} delay={i * 0.08}>
                <div className="border-l border-parchment/20 pl-6">
                  <p className="font-mono text-xs tracking-[0.22em] text-salvia-marco">{paso.n}</p>
                  <h3 className="mt-4 font-serif text-2xl">{paso.titulo}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-parchment/70">{paso.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ruta legal personal */}
      <section className="bg-parchment" data-testid="home-ruta-legal">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="border border-hairline bg-parchment-surface p-2.5">
                <img
                  src={IMAGES.ventanaNatural}
                  alt="Ventana con luz natural hacia el paisaje"
                  className="h-[300px] w-full object-cover sm:h-[400px]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-5">Ruta legal personal</p>
              <h2 className="font-serif text-3xl leading-tight tracking-tight text-ink-deep sm:text-4xl lg:text-5xl">
                Cada persona lleva un asunto distinto. Tú también.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-ink-muted">
                La ruta legal personal es nuestra forma de trabajar: primero entendemos tu momento de
                vida y tu asunto concreto; después te decimos qué área aplica, qué documentos
                necesitas y qué siguiente paso te conviene. Sin formularios infinitos ni respuestas de libro.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-ink-muted">
                Y una promesa honesta: si tu caso no lo podemos llevar, te lo decimos y te orientamos
                hacia dónde sí.
              </p>
              <div className="mt-8">
                <Link
                  to="/ruta-legal"
                  data-testid="home-ruta-legal-link"
                  className="inline-flex items-center gap-2 text-sm font-medium text-azul underline-offset-4 hover:underline"
                >
                  Conocer la ruta legal personal
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Siete servicios */}
      <section className="bg-parchment-surface/50" data-testid="home-servicios">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <Reveal>
            <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="eyebrow mb-5">Servicios</p>
                <h2 className="max-w-xl font-serif text-3xl leading-tight tracking-tight text-ink-deep sm:text-4xl lg:text-5xl">
                  Siete áreas, un mismo estándar
                </h2>
              </div>
              <Link
                to="/servicios"
                data-testid="home-servicios-ver-todos"
                className="inline-flex items-center gap-2 text-sm font-medium text-azul underline-offset-4 hover:underline"
              >
                Ver los siete servicios
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid gap-x-16 md:grid-cols-2">
            {[0, 1].map((col) => (
              <div key={col} className={col === 1 ? "md:mt-20" : ""}>
                {SERVICIOS.filter((_, i) => i % 2 === col).map((s, i) => (
                  <Reveal key={s.slug} delay={i * 0.05} y={16}>
                    <Link
                      to={`/servicios/${s.slug}`}
                      data-testid={`home-servicio-link-${s.slug}`}
                      className="group block border-t border-hairline py-7 transition-colors hover:bg-parchment"
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          <p className="font-mono text-[11px] tracking-[0.22em] text-azul">
                            {String(SERVICIOS.indexOf(s) + 1).padStart(2, "0")}
                          </p>
                          <h3 className="mt-2 font-serif text-2xl text-ink-deep sm:text-3xl">{s.nombre}</h3>
                          <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink-muted">{s.frase}</p>
                        </div>
                        <ArrowUpRight className="mt-2 h-5 w-5 shrink-0 text-ink-muted transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-azul" />
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compromisos */}
      <section className="bg-parchment" data-testid="home-compromisos">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <Reveal>
            <p className="eyebrow mb-5">Compromisos de atención</p>
            <h2 className="mb-14 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-ink-deep sm:text-4xl lg:text-5xl">
              Lo que puedes esperar de nosotros
              <br />
              (y lo que no prometemos)
            </h2>
          </Reveal>
          <div className="grid gap-x-16 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {COMPROMISOS.map((c, i) => {
              const Icono = COMPROMISO_ICONOS[i % COMPROMISO_ICONOS.length];
              return (
                <Reveal key={c.titulo} delay={Math.min(i * 0.05, 0.2)} y={16}>
                  <div className="flex gap-4">
                    <Icono className="mt-1 h-5 w-5 shrink-0 text-azul" strokeWidth={1.6} />
                    <div>
                      <h3 className="font-medium text-ink-deep">{c.titulo}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{c.detalle}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Guías */}
      <section className="bg-parchment" data-testid="home-guias">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:pb-24">
          <Reveal>
            <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="eyebrow mb-5">Guías</p>
                <h2 className="max-w-xl font-serif text-3xl leading-tight tracking-tight text-ink-deep sm:text-4xl">
                  Para leer antes de decidir
                </h2>
              </div>
              <Link to="/guias" data-testid="home-guias-ver-todas" className="inline-flex items-center gap-2 text-sm font-medium text-azul underline-offset-4 hover:underline">
                Todas las guías
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div>
            {GUIAS.slice(0, 3).map((g) => (
              <Reveal key={g.slug} y={16}>
                <Link
                  to={`/guias/${g.slug}`}
                  data-testid={`home-guia-link-${g.slug}`}
                  className="group flex items-center justify-between gap-6 border-t border-hairline py-6 transition-colors hover:bg-parchment-surface/70 sm:px-4"
                >
                  <div className="flex items-baseline gap-5 sm:gap-8">
                    <span className="hidden font-mono text-xs text-ink-muted sm:block">
                      {String(g.lecturaMin).padStart(2, "0")} min
                    </span>
                    <span>
                      <span className="block font-serif text-xl text-ink-deep sm:text-2xl">{g.titulo}</span>
                      <span className="mt-1 line-clamp-2 block max-w-xl text-sm text-ink-muted">{g.resumen}</span>
                    </span>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-ink-muted transition-all duration-300 group-hover:translate-x-1 group-hover:text-azul" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}
